import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { DateTimeFormattingService } from '@capital-access/common/formatting';
import { LocalizationService } from '@capital-access/common/localization';
import { ProfileRepository, ProfileType } from '@capital-access/profiles/common';
import { DEFAULT_DATE_FORMAT } from '../../constants';
import { LOCALIZATION_KEY } from '../../constants';
import { CreateProfileReportDto } from '../models/profile-report.models';
import { ProfileReportLayout } from '../models/profile-report-sections';

@Injectable()
export class ProfileReportNamingService {
  constructor(
    private dateFormatting: DateTimeFormattingService,
    private profileRepository: ProfileRepository,
    private localization: LocalizationService
  ) {}
  public getProfileReportFileName(
    showSrvSource: boolean,
    isSrv: boolean,
    createProfileRequest: CreateProfileReportDto,
    timezone: string,
    layout?: ProfileReportLayout
  ): Observable<string> {
    const currentDate = new Date();
    const ownershipSourceKey = isSrv ? 'surveillanceSource' : 'publicSource';

    return combineLatest([
      this.localization.localize(ownershipSourceKey, {}, { scope: LOCALIZATION_KEY }),
      this.dateFormatting.formatDate(currentDate.toISOString(), { zone: timezone }, DEFAULT_DATE_FORMAT),
      this.getProfileSpecificName(createProfileRequest, layout)
    ]).pipe(
      map(([ownershipSource, formattedDate, profileSpecificName]) => {
        const dataSource = showSrvSource ? `- (${ownershipSource}) ` : '';
        return `${profileSpecificName} ${dataSource}- ${formattedDate}`;
      }),
      take(1)
    );
  }

  private getProfileSpecificName(
    createProfileRequest: CreateProfileReportDto,
    layout?: ProfileReportLayout
  ): Observable<string> {
    const isSingleProfile = createProfileRequest.profiles.length === 1;

    if (!isSingleProfile) {
      const layoutKey = layout === ProfileReportLayout.OnePager ? ProfileReportLayout.OnePager : '';
      const reportNameKey =
        createProfileRequest.profileType === ProfileType.Institution
          ? `investor${layoutKey}ProfileReport`
          : `contact${layoutKey}ProfileReport`;

      return this.localization.localize(reportNameKey, {}, { scope: LOCALIZATION_KEY });
    }
    if (createProfileRequest.profiles[0].name) return of(createProfileRequest.profiles[0].name);

    switch (createProfileRequest.profileType) {
      case ProfileType.Contact: {
        return this.profileRepository.getContactInformation(createProfileRequest.profiles[0].id, ['name']).pipe(
          map(profile => {
            return profile.name!;
          })
        );
      }
      case ProfileType.Institution: {
        return this.profileRepository.getInstitutionInformation(createProfileRequest.profiles[0].id, ['name']).pipe(
          map(profile => {
            return profile.name!;
          })
        );
      }
      default:
        return of('');
    }
  }
}
